/*
The listing's own wholesale discount, worded exactly as the buyer reads it on a product card
(the buyer product card). The admin campaign popup shows it read-only, so an admin reviewing a
coupon can see what the listing is ALREADY giving away before approving more on top.

The five shapes come from models/discounts.js (`type`, kept in step with `name`):

    Same                  Buy 10 Get 2 Free
    SameAndDiscount       Buy 10 Get 2 Free, and 5% Off
    Discount              5% Off
    Different             Buy 10 Get 2 <other product> Free
    DifferentAndDiscount  Buy 10 Get 2 <other product> Free, and 5% Off

Anything else - 'nodiscount', '', null, a listing with no discount_id at all - has no label, and the
caller drops the field rather than printing "None": an absent discount is not a fact worth a row.

`discount_per` is stored as a STRING (models/discounts.js), and the buyer card is inconsistent about
it - one branch prints it raw, another calls .toFixed(2), which would throw on the stored type. Here
it goes through Number() once and trailing zeros are dropped, so a 5 reads "5% Off" and a 7.5 reads
"7.5% Off" rather than "5.00% Off".

`otherName` is the product given away by the two 'Different' shapes. It is a separate argument
rather than something read off `discount`, because how it is reached depends on how the discount was
joined - a saved campaign carries it through populate as
discount_on_product.inventory_id.product_id.name, while a pipeline that aggregates it would hand
over a resolved name of its own. Resolving it is the caller's job; wording it is this function's.
*/

// 5 -> '5', 7.5 -> '7.5', 18.00 -> '18'. An unparseable or non-positive percent gives ''.
const percent = (value) => {
  const n = Number(value);
  if (!isFinite(n) || n <= 0) return '';
  return `${Number(n.toFixed(2))}% Off`;
};

const lots = (discount, otherName) => {
  const on = discount.discount_on_product || {};
  const purchase = on.purchase;
  const bonus = on.bonus;
  if (!purchase || !bonus) return '';
  // The 'Different' shapes name the product being given away; without a resolved name they still
  // read correctly, just less specifically, rather than printing 'undefined'.
  const what = otherName ? `${otherName} ` : '';
  return `Buy ${purchase} Get ${bonus} ${what}Free`;
};

export const discountLabel = (discount, otherName) => {
  if (!discount) return '';
  // `type` is what the buyer card reads (activeInventory maps it to discount_type); `name` is the
  // same value on the same document, and older rows are likelier to carry only one of the two.
  const kind = discount.type || discount.name || '';

  if (kind === 'Discount') return percent(discount.discount_per);

  if (kind === 'Same' || kind === 'Different') return lots(discount, kind === 'Different' ? otherName : '');

  if (kind === 'SameAndDiscount' || kind === 'DifferentAndDiscount') {
    const free = lots(discount, kind === 'DifferentAndDiscount' ? otherName : '');
    const off = percent(discount.discount_per);
    // Either half may be missing on a half-filled row; whichever survives is still worth showing.
    if (free && off) return `${free}, and ${off}`;
    return free || off;
  }

  return '';
};

/*
The label for a saved campaign row, whose listing arrives through populate rather than a pipeline:
inventory_id.discount_id, with the giveaway product two hops further down.
*/
export const campaignDiscountLabel = (row) => {
  const listing = row && row.inventory_id;
  const discount = listing && listing.discount_id;
  if (!discount) return '';
  const on = discount.discount_on_product || {};
  const other = on.inventory_id && on.inventory_id.product_id;
  return discountLabel(discount, other && other.name);
};

export default discountLabel;
